// The face of a review card: what Oreia noticed, and the receipts.
//
// Deliberately verdict-free. The same card is read in the deck, where the buttons live outside it,
// and in the archive, where the verdict has already been given — so the card's only job is to make
// the claim checkable. That is also why the source chips are here and not optional: the promise the
// Self screen makes is that nothing is asserted about you that you cannot trace back to your own
// writing, and a card in a deck is exactly where someone will want to test that.

import { Link } from 'react-router-dom';
import { CitedObservation } from '../components/CitedObservation';
import { IconBook } from '../components/Icons';
import type { Observation } from '../api/portrait';

const KIND_LABEL: Record<string, string> = {
  pattern: 'Pattern',
  dream: 'From your dreams',
  waking: 'From your days',
  growth: 'Growth',
  tension: 'Pattern',
  observation: 'Pattern',
};

function kindClass(kind: string): string {
  return kind === 'dream' || kind === 'waking' || kind === 'growth' ? kind : 'pattern';
}

export interface ReviewCardProps {
  observation: Observation;
  entryTitle: (id: string) => string | null;
}

export function ReviewCard({ observation, entryTitle }: ReviewCardProps) {
  const sources = observation.sourceEntryIds;

  return (
    <article className="oreview-card">
      <div className="oreview-kind">
        <span className={`akind ${kindClass(observation.kind)}`}>
          <span className="d" aria-hidden />
          {KIND_LABEL[observation.kind] ?? 'Pattern'}
        </span>
        {observation.userEdited ? <span className="omono">in your words</span> : null}
      </div>

      <CitedObservation
        as="div"
        className="oreview-text"
        text={observation.text}
        citations={observation.citations}
      />

      {observation.trace ? <p className="oreview-trace">{observation.trace}</p> : null}

      <div className="oreview-srcs">
        <span className="olabel">Because you wrote</span>
        <div className="asrc">
          {sources.slice(0, 4).map((id, i) => (
            <Link className="schip" key={id} to={`/write/${encodeURIComponent(id)}`}>
              <IconBook />
              {entryTitle(id) || `Entry ${i + 1}`}
            </Link>
          ))}
        </div>
      </div>
    </article>
  );
}
